// Client dashboard, styled to the tryjobgpt.com home page: thin 1px borders,
// small crisp line icons in colour (no tinted boxes), compact Outfit type,
// soft shadows. Brand colours are TailorWiz navy and gold on grey.
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { ArrowRight, BarChart3, ClipboardCheck, Linkedin, Sparkles, TrendingUp } from "lucide-react";
import { LockedToolOverlay } from "@/components/LockedToolOverlay";
import { TailorComposer } from "@/components/dashboard/TailorComposer";
import { useToolAccess } from "@/hooks/useToolAccess";
import linkedinLogo from "@/assets/logos/linkedin-logo.png";

const NAVY = "#000F36";
const GOLD = "#CD9700";

interface Tool {
  toolId: string;
  title: string;
  description: string;
  icon: LucideIcon;
  iconColor: string;
  onClick: () => void;
}

interface ClientDashboardHomeProps {
  displayName: string;
  onUpgrade: () => void;
  onQualify: () => void;
  onResumeAnalysis: () => void;
  onLinkedIn: () => void;
  onMetrics: () => void;
}

function ToolCard({ tool }: { tool: Tool }) {
  const Icon = tool.icon;
  return (
    <LockedToolOverlay toolId={tool.toolId} onClick={tool.onClick} className="h-full">
      <div className="group h-full cursor-pointer rounded-xl border border-[#000F36]/40 bg-white transition-all hover:border-[#000F36]/70 hover:shadow-[0_4px_12px_-4px_rgba(0,15,54,0.15)]">
        <div className="flex items-center gap-2.5 px-4 py-2.5">
          <Icon className={`w-4 h-4 shrink-0 ${tool.iconColor}`} strokeWidth={2.25} />
          <h3 className="flex-1 text-[15px] font-bold text-[#000F36]">
            {tool.title}
          </h3>
          <ArrowRight className="w-3.5 h-3.5 text-slate-300 transition-all group-hover:text-[#CD9700] group-hover:translate-x-0.5" />
        </div>
        <div className="h-px bg-slate-200 mx-4" />
        <p className="px-4 pt-2.5 pb-3.5 text-[13px] leading-relaxed text-slate-700">{tool.description}</p>
      </div>
    </LockedToolOverlay>
  );
}

export function ClientDashboardHome(props: ClientDashboardHomeProps) {
  const { isToolLocked } = useToolAccess();
  const tools: Tool[] = [
    {
      toolId: "job-wizard",
      title: "See If You're Qualified",
      description: "Upload a job description and find out if you're truly qualified to apply in less than 60 seconds.",
      icon: ClipboardCheck,
      iconColor: "text-emerald-500",
      onClick: props.onQualify,
    },
    {
      toolId: "resume-analysis",
      title: "Test Your Resume Match",
      description: "Test your resume against a specific job to get a match score and a full breakdown of how well it aligns.",
      icon: BarChart3,
      iconColor: "text-violet-500",
      onClick: props.onResumeAnalysis,
    },
    {
      toolId: "linkedin-wizard",
      title: "Optimize Your LinkedIn Profile",
      description: "Create a high-impact LinkedIn profile that attracts opportunities.",
      icon: Linkedin,
      iconColor: "text-blue-500",
      onClick: props.onLinkedIn,
    },
    {
      toolId: "metrics-wizard",
      title: "Quantify Your Achievements",
      description: "Transform bullet points into powerful, quantified accomplishments.",
      icon: TrendingUp,
      iconColor: "text-orange-500",
      onClick: props.onMetrics,
    },
  ];

  return (
    <div className="min-h-full flex flex-col bg-[#F4F6F9] font-outfit">
      <div className="w-full max-w-[920px] mx-auto px-4 sm:px-8 py-10 flex-1">
        {/* LinkedIn follow */}
        <div className="flex justify-end mb-2">
          <a
            href="https://www.linkedin.com/in/resultsdrivenresumes"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white pl-1 pr-3.5 py-1 text-[12.5px] font-semibold text-slate-600 transition-colors hover:border-[#0A66C2] whitespace-nowrap"
          >
            <span className="bg-[#0A66C2] rounded-full p-1">
              <img src={linkedinLogo} alt="" className="w-3.5 h-3.5" />
            </span>
            <span><span className="font-bold" style={{ color: NAVY }}>100,000+</span> followers</span>
            <span className="font-bold text-[#0A66C2]">Follow</span>
          </a>
        </div>

        {/* Start tailoring right here, like a JobGPT search */}
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em]" style={{ color: GOLD }}>
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.25} /> Resume tailoring
          </span>
          <h1 className="mt-2 text-[28px] leading-[1.1] font-extrabold tracking-tight" style={{ color: NAVY }}>
            Tailor Your Resume to ANY Job in Minutes.
          </h1>
          <p className="mt-2 text-[15px] text-slate-700">
            Paste the job description, attach your resume, and tailor it honestly and accurately in minutes.
          </p>
        </div>
        <div className="mb-10">
          <TailorComposer locked={isToolLocked("resume-tailoring")} onLocked={props.onUpgrade} />
        </div>

        {/* Tools */}
        <p className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-400">Your tools</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {tools.map((t) => (
            <ToolCard key={t.toolId} tool={t} />
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-[920px] mx-auto px-4 sm:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-[12.5px] font-semibold text-slate-500">
            <Link to="/privacy-policy" className="hover:text-[#000F36]">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-[#000F36]">Terms of Service</Link>
            <Link to="/help-center" className="hover:text-[#000F36]">Help Center</Link>
            <Link to="/faq" className="hover:text-[#000F36]">FAQ</Link>
            <Link to="/about" className="hover:text-[#000F36]">About Us</Link>
            <Link to="/services" className="hover:text-[#000F36]">Request Services</Link>
          </nav>
          <p className="text-[12.5px] text-slate-400">© {new Date().getFullYear()} TailorWiz. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
